import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Check, ClipboardCheck, Route as RouteIcon, MessageCircle, Sun, Clock12, Moon, ShieldCheck, Sparkles } from "lucide-react";
import { ValueMath, WebinarList } from "@/components/WebinarBonus";
import { api } from "@/lib/api";
import { track } from "@/lib/track";
import { DIM_STYLE } from "@/components/Badges";
import { ContactLink } from "@/components/ContactDialog";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const up = { hidden: { opacity: 0, y: 24 }, show: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.08, duration: 0.6, ease: [0.22, 1, 0.36, 1] } }) };
const Reveal = ({ children, i = 0, className = "" }) => (
  <motion.div variants={up} custom={i} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.2 }} className={className}>{children}</motion.div>
);

const SCREENS = [
  ["screen_test", "Il test: 18 domande, 3 minuti"],
  ["screen_report", "Il tuo profilo, tra 18"],
  ["screen_oggi", "Ogni giorno: Mattino, Mezzogiorno, Sera"],
  ["screen_percorso", "Il percorso di 7 giorni"],
  ["screen_coach", "Coach Mino, il coach AI"],
];
const AREAS = [
  ["direzione", "Direzione", "Sapere cosa conta davvero"],
  ["continuita", "Continuità", "Mantenere le buone abitudini"],
  ["spazio", "Spazio", "Ritagliare tempo per te"],
  ["ricarica", "Ricarica", "Recuperare energia"],
  ["motivazione", "Motivazione", "Ritrovare il tuo perché"],
  ["mentalita", "Mentalità", "Parlarti con più fiducia"],
];
const FAQ = [
  ["Pago qualcosa durante la prova?", "No. I primi 7 giorni sono gratuiti. Puoi disdire prima della fine dal tuo profilo, in due tocchi, senza alcun addebito."],
  ["Quanto tempo mi serve al giorno?", "Bastano 5-10 minuti, divisi in tre momenti: Mattino, Mezzogiorno e Sera. Ti arriva un promemoria agli orari che scegli tu."],
  ["Devo scaricare qualcosa dagli store?", "No. Coach2Go si apre dal browser e si aggiunge alla schermata Home del telefono in pochi tocchi: funziona come un'app."],
  ["Cos'è Coach Mino?", "È il coach digitale basato sul Metodo Mino Cesari: conosce il tuo profilo e il tuo percorso e ti aiuta con domande e piccole azioni concrete. È un'intelligenza artificiale e non sostituisce medici o psicologi."],
  ["È una terapia?", "No. Coach2Go è un percorso di crescita personale e mental coaching, non un servizio medico o psicologico."],
];

function useCta() {
  const navigate = useNavigate();
  return () => {
    track("lp_cta");
    navigate("/test");
  };
}

function CtaButton({ testId, label = "Prova 7 giorni gratis", className = "" }) {
  const go = useCta();
  return (
    <button onClick={go} data-testid={testId} className={`btn-gold group inline-flex items-center justify-center gap-2 rounded-full px-7 py-4 text-base font-semibold uppercase tracking-wide transition-transform duration-200 active:scale-[0.98] ${className}`}>
      {label} <ArrowRight size={18} className="transition-transform duration-200 group-hover:translate-x-1" />
    </button>
  );
}

function Header() {
  const go = useCta();
  return (
    <header className="sticky top-0 z-30 border-b border-[#27272A]/60 bg-[#09090B]/80 backdrop-blur-xl">
      <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-3">
        <img src="/logo-horizontal.png" alt="Coach2Go" className="h-7 w-auto" data-testid="lp-logo" />
        <button onClick={go} data-testid="lp-header-cta" className="rounded-full border border-[#D4AF37]/60 px-4 py-2 text-sm font-semibold text-[#E5C158] transition-colors hover:bg-[#D4AF37]/10">
          Prova gratis
        </button>
      </div>
    </header>
  );
}

function Hero() {
  return (
    <section className="relative overflow-hidden" data-testid="lp-hero">
      <img src="/lp/hero.webp" alt="" className="absolute inset-0 h-full w-full object-cover object-[70%_center] opacity-60 md:left-auto md:w-1/2 md:opacity-90" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#09090B] via-[#09090B]/70 to-[#09090B]/30 md:bg-gradient-to-r md:from-[#09090B] md:via-[#09090B]/95 md:to-transparent" />
      <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-[#09090B] to-transparent" />
      <div className="relative mx-auto max-w-6xl px-5 pb-16 pt-24 md:pb-28 md:pt-32">
        <div className="max-w-xl">
          <motion.p variants={up} initial="hidden" animate="show" className="eyebrow">Metodo Mino Cesari</motion.p>
          <motion.h1 variants={up} custom={1} initial="hidden" animate="show" data-testid="lp-hero-headline" className="font-serif-el mt-3 text-4xl font-semibold leading-[1.05] text-[#FAF9F6] sm:text-5xl lg:text-6xl">
            Il tuo coach, ogni giorno. <span className="italic text-[#E5C158]">In tasca.</span>
          </motion.h1>
          <motion.p variants={up} custom={2} initial="hidden" animate="show" className="mt-5 text-base leading-relaxed text-[#D4D4D8] md:text-lg">
            Un test di 3 minuti, un profilo su misura e 7 giorni di piccole azioni quotidiane per ritrovare direzione, energia e costanza.
          </motion.p>
          <motion.div variants={up} custom={3} initial="hidden" animate="show" className="mt-8">
            <CtaButton testId="lp-hero-cta" className="w-full sm:w-auto" />
            <p className="mt-3 flex items-center gap-2 text-sm text-[#B4B4BC]"><ShieldCheck size={15} className="text-[#D4AF37]" /> 7 giorni gratis · disdici quando vuoi</p>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function Problem() {
  return (
    <section className="mx-auto grid max-w-6xl items-center gap-10 px-5 py-20 md:grid-cols-2" data-testid="lp-problem">
      <Reveal>
        <p className="eyebrow">Ti riconosci?</p>
        <h2 className="font-serif-el mt-3 text-3xl font-semibold leading-tight text-[#FAF9F6] md:text-4xl">Sai cosa vorresti cambiare. Il difficile è farlo ogni giorno.</h2>
        <ul className="mt-6 space-y-3 text-base text-[#D4D4D8]">
          {["Giornate piene, ma la sensazione di non arrivare a ciò che conta", "Buoni propositi che durano una settimana", "Poco tempo ed energia per te"].map((t) => (
            <li key={t} className="flex gap-3"><span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-[#D4AF37]" />{t}</li>
          ))}
        </ul>
        <p className="mt-6 text-base leading-relaxed text-[#B4B4BC]">Coach2Go non ti dà altra teoria: ti accompagna con un passo concreto al mattino, uno durante la giornata e uno la sera.</p>
      </Reveal>
      <Reveal i={1}><img src="/lp/chaos.webp" alt="" className="w-full rounded-3xl border border-[#27272A] object-cover" /></Reveal>
    </section>
  );
}

function Steps() {
  const steps = [
    [ClipboardCheck, "Fai il test", "18 domande, 3 minuti. Misuriamo 6 aree della tua vita quotidiana."],
    [RouteIcon, "Scopri il tuo profilo", "Uno dei 18 profili Coach2Go, partendo dall'area dove hai più margine di crescita."],
    [Sparkles, "Vivi i tuoi 7 giorni", "Mattino, Mezzogiorno e Sera: un'azione e una domanda al giorno, con Coach Mino al tuo fianco."],
  ];
  return (
    <section className="border-y border-[#27272A]/60 bg-[#0D0D0F]" data-testid="lp-steps">
      <div className="mx-auto max-w-6xl px-5 py-20">
        <Reveal><p className="eyebrow">Come funziona</p><h2 className="font-serif-el mt-3 text-3xl font-semibold text-[#FAF9F6] md:text-4xl">Tre passi, nessuna complicazione</h2></Reveal>
        <div className="mt-10 grid gap-5 md:grid-cols-3">
          {steps.map(([Icon, t, d], i) => (
            <Reveal key={t} i={i} className="rounded-3xl border border-[#27272A] bg-[#121215] p-6">
              <div className="flex items-center gap-3">
                <span className="tabular-nums text-sm text-[#D4AF37]">0{i + 1}</span>
                <Icon size={22} className="text-[#E5C158]" />
              </div>
              <h3 className="font-serif-el mt-4 text-2xl font-semibold text-[#FAF9F6]">{t}</h3>
              <p className="mt-2 text-base leading-relaxed text-[#B4B4BC]">{d}</p>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function Phone({ src, label }) {
  return (
    <figure className="w-[230px] shrink-0 snap-center md:w-[250px]">
      <div className="rounded-[2.4rem] border border-[#3F3F46] bg-black p-2 shadow-[0_30px_80px_-20px_rgba(212,175,55,0.25)] ring-1 ring-[#D4AF37]/20">
        <img src={`/lp/${src}.webp`} alt={label} loading="lazy" className="aspect-[390/844] w-full rounded-[1.9rem] object-cover object-top" />
      </div>
      <figcaption className="mt-3 text-center text-sm text-[#B4B4BC]">{label}</figcaption>
    </figure>
  );
}

function Screens() {
  const ref = useRef(null);
  return (
    <section className="py-20" data-testid="lp-screens">
      <Reveal className="mx-auto max-w-6xl px-5"><p className="eyebrow">Dentro l'app</p><h2 className="font-serif-el mt-3 text-3xl font-semibold text-[#FAF9F6] md:text-4xl">Guarda come funziona</h2></Reveal>
      <div ref={ref} className="no-scrollbar mt-10 flex snap-x snap-mandatory gap-6 overflow-x-auto px-5 pb-4 min-[1440px]:justify-center" data-testid="lp-screens-carousel">
        {SCREENS.map(([s, l]) => <Phone key={s} src={s} label={l} />)}
      </div>
    </section>
  );
}

function Moments() {
  const m = [[Sun, "Mattino", "Un'intenzione per iniziare"], [Clock12, "Mezzogiorno", "Un'azione concreta"], [Moon, "Sera", "Una domanda per riflettere"]];
  return (
    <section className="mx-auto max-w-6xl px-5 py-10" data-testid="lp-moments">
      <Reveal><img src="/lp/moments.webp" alt="Mattino, Mezzogiorno e Sera" className="aspect-[16/6] w-full rounded-3xl border border-[#27272A] object-cover" /></Reveal>
      <div className="mt-6 grid grid-cols-3 gap-3">
        {m.map(([Icon, t, d], i) => (
          <Reveal key={t} i={i} className="text-center">
            <Icon size={20} className="mx-auto text-[#D4AF37]" />
            <p className="mt-2 text-xl font-semibold text-[#FAF9F6]">{t}</p>
            <p className="mt-1 text-xs text-[#B4B4BC] sm:text-sm">{d}</p>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function Areas() {
  return (
    <section className="mx-auto max-w-6xl px-5 py-20" data-testid="lp-areas">
      <Reveal><p className="eyebrow">Le 6 aree</p><h2 className="font-serif-el mt-3 text-3xl font-semibold text-[#FAF9F6] md:text-4xl">Partiamo da dove ti serve di più</h2></Reveal>
      <div className="mt-10 grid grid-cols-2 gap-3 md:grid-cols-3">
        {AREAS.map(([k, t, d], i) => {
          const { c, icon: Icon } = DIM_STYLE[k];
          return (
            <Reveal key={k} i={i % 3} className="rounded-2xl border border-[#27272A] bg-[#121215] p-5 transition-transform duration-300 hover:-translate-y-1">
              <Icon size={22} style={{ color: c }} />
              <p className="mt-3 text-xl font-semibold text-[#FAF9F6]">{t}</p>
              <p className="mt-1 text-sm text-[#B4B4BC]">{d}</p>
            </Reveal>
          );
        })}
      </div>
    </section>
  );
}

function CoachBlock() {
  return (
    <section className="border-y border-[#27272A]/60 bg-[#0D0D0F]" data-testid="lp-coach">
      <div className="mx-auto grid max-w-6xl items-center gap-10 px-5 py-20 md:grid-cols-2">
        <Reveal className="order-2 flex justify-center md:order-1"><Phone src="screen_coach" label="Coach Mino" /></Reveal>
        <Reveal i={1} className="order-1 md:order-2">
          <p className="eyebrow">Coach Mino</p>
          <h2 className="font-serif-el mt-3 text-3xl font-semibold leading-tight text-[#FAF9F6] md:text-4xl">Quando ti blocchi, hai qualcuno a cui chiedere</h2>
          <p className="mt-5 text-base leading-relaxed text-[#D4D4D8]">Coach Mino è il coach digitale basato sul Metodo Mino Cesari. Conosce il tuo profilo e il tuo percorso e ti risponde con una domanda o un piccolo passo concreto.</p>
          <p className="mt-4 flex items-center gap-2 text-sm text-[#B4B4BC]"><MessageCircle size={15} className="text-[#D4AF37]" /> Incluso nell'abbonamento</p>
        </Reveal>
      </div>
    </section>
  );
}

function Pricing() {
  const [plans, setPlans] = useState([]);
  useEffect(() => { api.get("/payments/plans").then(({ data }) => setPlans([...(data.plans || [])].sort((a, b) => (b.id === "annual") - (a.id === "annual")))).catch(() => {}); }, []);
  return (
    <section className="mx-auto max-w-6xl px-5 py-20" data-testid="lp-pricing">
      <Reveal className="text-center"><p className="eyebrow">Prezzi</p><h2 className="font-serif-el mt-3 text-3xl font-semibold text-[#FAF9F6] md:text-4xl">7 giorni gratis, poi scegli tu</h2></Reveal>
      <div className="mx-auto mt-10 grid max-w-3xl gap-4 md:grid-cols-2">
        {plans.map((p, i) => (
          <Reveal key={p.id} i={i} className={`relative rounded-3xl border p-6 ${p.id ==="annual" ? "border-[#D4AF37]/60 bg-gradient-to-b from-[#D4AF37]/10 to-[#121215]" : "border-[#27272A] bg-[#121215]"}`}>
            <div data-testid={`lp-plan-${p.id}`}>
              {p.savings && <span className="absolute -top-3 right-5 rounded-full bg-[#D4AF37] px-3 py-1 text-xs font-bold uppercase text-[#09090B]">{p.savings}</span>}
              <p className="text-sm font-semibold uppercase tracking-wider text-[#B4B4BC]">{p.id === "annual" ? "Annuale" : "Mensile"}</p>
              <p className="mt-2 font-serif-el text-4xl font-semibold text-[#FAF9F6]">{p.price}<span className="text-lg text-[#B4B4BC]">/{p.period}</span></p>
              {p.monthly_equivalent && <p className="mt-1 text-sm text-[#E5C158]">pari a {p.monthly_equivalent}</p>}
              <ul className="mt-5 space-y-2 text-sm text-[#D4D4D8]">
                {["7 giorni gratis", "Percorso completo su misura", "Coach Mino incluso", "Disdici quando vuoi"].map((t) => <li key={t} className="flex gap-2"><Check size={16} className="text-[#D4AF37]" />{t}</li>)}
              </ul>
              {p.id === "annual" && <><WebinarList className="mt-5" /><ValueMath className="mt-3" /></>}
            </div>
          </Reveal>
        ))}
      </div>
      <div className="mt-10 text-center"><CtaButton testId="lp-pricing-cta" /></div>
    </section>
  );
}

function Faq() {
  return (
    <section className="mx-auto max-w-3xl px-5 pb-20" data-testid="lp-faq">
      <Reveal><h2 className="font-serif-el text-3xl font-semibold text-[#FAF9F6]">Domande frequenti</h2></Reveal>
      <Accordion type="single" collapsible className="mt-6">
        {FAQ.map(([q, a], i) => (
          <AccordionItem key={q} value={`f${i}`} className="border-[#27272A]">
            <AccordionTrigger data-testid={`lp-faq-${i}`} className="text-left text-base text-[#FAF9F6] hover:no-underline">{q}</AccordionTrigger>
            <AccordionContent className="text-base leading-relaxed text-[#B4B4BC]">{a}</AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </section>
  );
}

function FinalCta() {
  return (
    <section className="relative overflow-hidden border-t border-[#27272A]/60 px-5 py-24 text-center" data-testid="lp-final">
      <div className="absolute left-1/2 top-0 h-64 w-64 -translate-x-1/2 rounded-full bg-[#D4AF37]/15 blur-3xl" />
      <Reveal className="relative mx-auto max-w-2xl">
        <h2 className="font-serif-el text-4xl font-semibold leading-tight text-[#FAF9F6] md:text-5xl">Il cambiamento non arriva in un giorno. <span className="italic text-[#E5C158]">Arriva ogni giorno.</span></h2>
        <div className="mt-8"><CtaButton testId="lp-final-cta" /></div>
        <div className="mt-10"><ContactLink testId="lp-contact-link" /></div>
      </Reveal>
    </section>
  );
}

function StickyBar({ visible }) {
  return (
    <div className={`fixed inset-x-0 bottom-0 z-40 border-t border-[#27272A] bg-[#09090B]/90 p-3 backdrop-blur-xl transition-transform duration-300 md:hidden ${visible ?"translate-y-0" : "translate-y-full"}`} data-testid="lp-sticky-bar">
      <CtaButton testId="lp-sticky-cta" className="w-full py-3.5" />
    </div>
  );
}

export default function LandingLP() {
  const scroller = useRef(null);
  const [sticky, setSticky] = useState(false);
  useEffect(() => { track("lp_view"); }, []);
  return (
    <div ref={scroller} onScroll={(e) => setSticky(e.currentTarget.scrollTop > 500)} className="fixed inset-0 z-40 overflow-y-auto bg-[#09090B]" data-testid="lp-page">
      <Header />
      <Hero />
      <Problem />
      <Steps />
      <Screens />
      <Moments />
      <Areas />
      <CoachBlock />
      <Pricing />
      <Faq />
      <FinalCta />
      <div className="h-20 md:hidden" />
      <StickyBar visible={sticky} />
    </div>
  );
}
